import { LoggerService } from 'app/logger/logger.service';
import { DateObjectUnits, DateTime, DateTimeFormatOptions } from 'luxon';

/**
 * @returns Formatter: short localized date and time with seconds
 * @example 8/6/2014, 1:07:04 PM
 */
export function formatTimeWithRegionAndTimezone(date: Date | string, region: string, logger: LoggerService, timezone?: string): string {
  date = ensureDateObj(date, logger);
  return DateTime.fromJSDate(date, timezone ? { zone: timezone } : undefined)
    .toFormat('F', { locale: region });
}

/**
 * @param formattedStr Formatter: short localized date and time with seconds
 * @example 8/6/2014, 1:07:04 PM
 */
export function getDateFromFormattedWithRegionAndTimezone(formattedStr: string, region: string, timezone?: string): Date {
  const d = DateTime.fromFormat(formattedStr, 'F', { locale: region, zone: timezone });
  return d.setLocale(region).toJSDate();
}

/**
 * @returns Formatter: localized numeric date
 * @example 9/4/2017
 */
export function formatDateWithRegionAndTimezone(date: Date, region: string, logger: LoggerService, timezone?: string): string {
  date = ensureDateObj(date, logger);
  return DateTime.fromJSDate(date, timezone ? { zone: timezone } : undefined)
    .toFormat('D', { locale: region });
}

function ensureDateObj(date: Date | string, logger: LoggerService): Date {
  if (Object.prototype.toString.call(date) === '[object Date]') {
    return date as Date;
  } else if (typeof date === 'string') {
    return new Date(date);
  } else {
    logger.error('time-format.util', 'unknown type of date param');
    return date;
  }
}

/**
 * Create a JS Date in given timezone, which each part is exactly the same value with input
 */
export function createJSDateFromObjWithTimezone(obj: DateObjectUnits, timezone: string): Date {
  return DateTime.fromObject(obj, { zone: timezone }).toJSDate();
}

/**
 * Create a JS Date in UTC timezone, which each part is exactly the same value with input
 */
export function createJSDateFromObjInUTC(obj: DateObjectUnits): Date {
  return createJSDateFromObjWithTimezone(obj, 'UTC');
}

/**
 * Create a JS Date in local timezone, which each part is exactly the same value with input
 */
export function createJSDateFromObjInLocal(obj: DateObjectUnits): Date {
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return createJSDateFromObjWithTimezone(obj, localTimezone);
}

/**
 * Get utc parts of a Date object
 */
export function getUTCInfo(date: Date): Pick<DateTime, 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second'> {
  const {
    year,
    month,
    day,
    hour,
    minute,
    second
  } = DateTime.fromJSDate(date, { zone: 'UTC' });
  return {
    year,
    month,
    day,
    hour,
    minute,
    second
  };
}

export function getFormatFromLocaleTimeString({ format, locale }: { format: DateTimeFormatOptions, locale: string }): string {
  const luxonFormat = DateTime.parseFormatForOpts(format, { locale });
  return luxonFormat;
}

